import type { settingTypes } from '@ipfabric/utils';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { FormikVendorApiUsernamePasswordFields } from './fields/FormikVendorApiUsernamePasswordFields';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

export type PrismaFormValues =
  BaseVendorFormValues<settingTypes.PrismaApiSettings>;

type Props = VendorDialogProps<
  PrismaFormValues,
  settingTypes.PrismaApiSettings,
  settingTypes.UpdatePrismaApiSettings
>;

const resetSecretAttributes = (values: PrismaFormValues): PrismaFormValues => {
  return {
    ...values,
    username: '',
    password: '',
    tsgid: '',
  };
};

const BasePrismaForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const formik = useFormik<PrismaFormValues>({
    initialValues: resetSecretAttributes(props.initialValues),
    validate: (values) => {
      const { required } = msg.discoverySettings.vendorsApi.validations;
      const errors: FormikErrors<PrismaFormValues> = {};
      if (!editMode && !values.username) {
        errors.username = required;
      }
      if (!editMode && !values.password) {
        errors.password = required;
      }
      if (!editMode && !values.tsgid) {
        errors.tsgid = required;
      }
      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit((values: PrismaFormValues) => {
      const formattedValues = {
        ...values,
        ...getAdvancedSettingsDefaultValues(values),
      };

      if (isVendorUpdate(values) && props.dialogType === 'edit') {
        props.onSubmit({
          ...formattedValues,
          id: values.id,
          username:
            values.username || props.initialValues.username || undefined,
          password:
            values.password || props.initialValues.password || undefined,
          tsgid: values.tsgid || props.initialValues.tsgid || undefined,
        });
      } else if (props.dialogType === 'add') {
        props.onSubmit(formattedValues);
      }
    }),
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<PrismaFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikVendorApiUsernamePasswordFields
        formik={formik}
        isEditMode={editMode}
      />
      <FormikTextField
        formik={formik}
        label={msg.discoverySettings.vendorsApi.prismaForm.tsgid}
        placeholder={editMode ? '(unchanged)' : undefined}
        name='tsgid'
        type='text'
      />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikVendorApiRejectUnauthorizedField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const PrismaForm = memo(BasePrismaForm);
